import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { TbCheck } from "react-icons/tb";
import type { CurrentSyncFile } from "src/context/PraxisSoftwareSyncContext";
import "./praxisSoftwareSyncStatusBadge.scss";

export type SyncStatusPhase = "syncing" | "done";

/** Persistent, non-blocking bottom-right indicator — covers both the foreground batch
 *  (also covered by the blocking overlay) AND silent background auto-parse runs, which
 *  the blocking overlay deliberately never shows for. The actual "results popup shows
 *  success but the data isn't there yet" bug was a missing cache invalidation on the
 *  XHR upload path (see padUploadService.ts) — this badge doesn't fix that, it answers
 *  the trust question staff raised on top of it: once this says done, the invalidation
 *  has already run, so navigating anywhere now shows current data. */
export function PraxisSoftwareSyncStatusBadge({
    phase,
    currentFile,
    batchProgress,
}: {
    phase: SyncStatusPhase;
    currentFile: CurrentSyncFile | null;
    batchProgress: { index: number; total: number } | null;
}) {
    const { t } = useTranslation();

    return createPortal(
        <div className={`pvs-sync-badge pvs-sync-badge--${phase}`} role="status" aria-live="polite">
            {phase === "syncing" ? (
                <>
                    <div className="pvs-sync-badge__spinner" />
                    <div className="pvs-sync-badge__text">
                        <span className="pvs-sync-badge__title">{t("praxisSoftwareSync.badgeSyncing")}</span>
                        <span className="pvs-sync-badge__detail">
                            {batchProgress
                                ? t("praxisSoftwareSync.blockingStep", { index: batchProgress.index + 1, total: batchProgress.total })
                                : ""}
                            {currentFile ? ` · ${currentFile.filename}` : ""}
                        </span>
                    </div>
                </>
            ) : (
                <>
                    <span className="pvs-sync-badge__check"><TbCheck /></span>
                    <span className="pvs-sync-badge__title">{t("praxisSoftwareSync.badgeDone")}</span>
                </>
            )}
        </div>,
        document.body,
    );
}
